/**
 * Intake Page - Main container component
 * Orchestrates all intake-related components and state
 */

import { useEffect, useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Edit, ShoppingBasket, ShoppingCart } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/_core/hooks/usePermissions";

// Components
import { IntakeFilters } from "./components/IntakeFilters";
import { IntakeSummary, type MacroTargets } from "./components/IntakeSummary";
import { MealCategoryCard } from "./components/MealCategoryCard";
import { SpajzaDialog } from "./components/SpajzaDialog";
import { CopyIntakeDialog } from "./components/CopyIntakeDialog";
import { IntakePrint } from "./components/IntakePrint";
import { ShoppingListDialog } from "./components/ShoppingListDialog";

// Hooks
import { useIntakeData } from "./hooks/useIntakeData";
import { useIntakePrint } from "./hooks/useIntakePrint";

// Utils
import { calculateNutrition } from "./utils";

// Types
import type {
  Prijem,
  NutritionalValues,
  MealCategoryTotals,
  DailyTotals,
} from "./types";

export default function IntakePage() {
  const { t } = useTranslation();
  const { canWriteIntake } = usePermissions();

  // State
  const [selectedKlientId, setSelectedKlientId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState<Prijem | null>(null);
  const [addingToKategoria, setAddingToKategoria] = useState<number | null>(
    null
  );
  const [previewPotravinaId, setPreviewPotravinaId] = useState<number | null>(
    null
  );
  const [previewAmount, setPreviewAmount] = useState<string>("100");

  // Pantry dialog
  const [spajzaOpen, setSpajzaOpen] = useState(false);

  // Shopping list dialog
  const [shoppingListOpen, setShoppingListOpen] = useState(false);

  // Copy dialog
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);

  // Custom hook
  const {
    klienti,
    prijemKategorie,
    potraviny,
    prijemByKategoria,
    dailyTotals,
    isLoading,
    createIntake,
    updateIntake,
    deleteIntake,
    getMealTotals,
  } = useIntakeData({ selectedKlientId, selectedDate });

  const utils = trpc.useUtils();

  // Fetch macro targets effective for the selected intake date.
  // The backend returns the latest measurement target on or before selectedDate.
  const { data: macroTargets } = trpc.meranie.getLatestTargets.useQuery(
    { klientId: selectedKlientId!, datum: selectedDate },
    { enabled: !!selectedKlientId }
  );

  // ── Print ──────────────────────────────────────────────────────────────────

  const {
    printSettings,
    setPrintSettings,
    toggleAllColumns,
    areAllColumnsSelected,
    handlePrint,
  } = useIntakePrint();

  const allPrijem = useMemo(
    () => Object.values(prijemByKategoria).flat(),
    [prijemByKategoria]
  );

  useEffect(() => {
    if (klienti.length === 1 && selectedKlientId !== klienti[0].id) {
      setSelectedKlientId(klienti[0].id);
    }
  }, [klienti, selectedKlientId]);

  const handlePrintClick = () => {
    const clientAlias =
      klienti.find(k => k.id === selectedKlientId)?.alias ??
      String(selectedKlientId);
    handlePrint(allPrijem, prijemKategorie, clientAlias, selectedDate);
  };

  // ── tRPC mutations for reorder / move ──────────────────────────────────────

  const reorderMutation = trpc.prijem.reorder.useMutation({
    onSuccess: () => {
      utils.prijem.getByKlientAndDate.invalidate();
    },
  });

  const moveKategoriaMutation = trpc.prijem.moveKategoria.useMutation({
    onSuccess: () => {
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
    },
  });

  // ── Copy helpers ───────────────────────────────────────────────────────────

  const handleCopied = () => {
    utils.prijem.getByKlientAndDate.invalidate();
    utils.prijem.getDailyTotals.invalidate();
  };

  // ── Food handlers ──────────────────────────────────────────────────────────

  const handleAddFood = async (
    kategoriaId: number,
    potravinaId: number | null,
    receptId: number | null,
    amount: string,
    notes: string
  ) => {
    if (!selectedKlientId) return;
    const success = await createIntake({
      klientId: selectedKlientId,
      datum: selectedDate,
      potravinaId: potravinaId ?? undefined,
      receptId: receptId ?? undefined,
      hmotnost: amount,
      prijemKategoriaId: kategoriaId,
      poznamka: notes,
    });
    if (success) {
      setAddingToKategoria(null);
      setPreviewPotravinaId(null);
      setPreviewAmount("100");
    }
  };

  const handlePreviewChange =
    (kategoriaId: number) => (potravinaId: number | null, amount: string) => {
      setAddingToKategoria(kategoriaId);
      setPreviewPotravinaId(potravinaId);
      setPreviewAmount(amount);
    };

  const handleEditFood = (item: Prijem) => {
    setEditingItem({
      ...item,
      hmotnost: item.hmotnost || "100",
      poznamka: item.poznamka || "",
    });
  };

  const handleUpdateFood = async (
    itemId: number,
    hmotnost: string,
    poznamka: string
  ) => {
    const success = await updateIntake(itemId, { hmotnost, poznamka });
    if (success) setEditingItem(null);
  };

  const handleDeleteFood = async (id: number) => {
    await deleteIntake(id);
  };

  // ── Reorder / Move handlers ────────────────────────────────────────────────

  const handleReorder = useCallback(
    (orderedIds: number[]) => {
      reorderMutation.mutate({ orderedIds });
    },
    [reorderMutation]
  );

  const handleMoveToKategoria = useCallback(
    (prijemId: number, newKategoriaId: number) => {
      if (!selectedKlientId) return;
      moveKategoriaMutation.mutate({
        prijemId,
        newKategoriaId,
        klientId: selectedKlientId,
        datum: selectedDate,
      });
    },
    [moveKategoriaMutation, selectedKlientId, selectedDate]
  );

  // ── Projected nutrition ────────────────────────────────────────────────────

  const getProjectedNutrition = (
    kategoriaId: number
  ): NutritionalValues | null => {
    if (addingToKategoria !== kategoriaId || !previewPotravinaId) return null;
    const potravina = potraviny.find(p => p.id === previewPotravinaId);
    if (!potravina) return null;
    return calculateNutrition(potravina, previewAmount);
  };

  const projectedDailyTotals: DailyTotals | null = useMemo(() => {
    if (!previewPotravinaId || !dailyTotals) return null;
    const potravina = potraviny.find(p => p.id === previewPotravinaId);
    if (!potravina) return null;
    const projected = calculateNutrition(potravina, previewAmount);
    const dt = dailyTotals as DailyTotals & {
      totalNmkG?: number | string;
      totalVlakninaG?: number | string;
      totalSodikG?: number | string;
    };
    return {
      totalEnergiaKcal: (
        parseFloat(String(dt.totalEnergiaKcal || 0)) +
        parseFloat(projected.energiaKcal)
      ).toFixed(1),
      totalProteinG: (
        parseFloat(String(dt.totalProteinG || 0)) +
        parseFloat(projected.proteinG)
      ).toFixed(1),
      totalTukG: (
        parseFloat(String(dt.totalTukG || 0)) + parseFloat(projected.tukG)
      ).toFixed(1),
      totalSacharidG: (
        parseFloat(String(dt.totalSacharidG || 0)) +
        parseFloat(projected.sacharidG)
      ).toFixed(1),
      totalNmkG: (
        parseFloat(String(dt.totalNmkG || 0)) + parseFloat(projected.nmkG)
      ).toFixed(1),
      totalVlakninaG: (
        parseFloat(String(dt.totalVlakninaG || 0)) +
        parseFloat(projected.vlakninaG)
      ).toFixed(1),
      totalSodikG: (
        parseFloat(String(dt.totalSodikG || 0)) + parseFloat(projected.sodikG)
      ).toFixed(2),
    };
  }, [previewPotravinaId, previewAmount, dailyTotals, potraviny]);

  const getProjectedMealTotals = (
    kategoriaId: number
  ): MealCategoryTotals | null => {
    const projected = getProjectedNutrition(kategoriaId);
    if (!projected) return null;
    const currentTotals = getMealTotals(kategoriaId);
    return {
      totalEnergiaKcal: (
        parseFloat(currentTotals.totalEnergiaKcal) +
        parseFloat(projected.energiaKcal)
      ).toFixed(1),
      totalProteinG: (
        parseFloat(currentTotals.totalProteinG) + parseFloat(projected.proteinG)
      ).toFixed(1),
      totalTukG: (
        parseFloat(currentTotals.totalTukG) + parseFloat(projected.tukG)
      ).toFixed(1),
      totalSacharidG: (
        parseFloat(currentTotals.totalSacharidG) +
        parseFloat(projected.sacharidG)
      ).toFixed(1),
    };
  };

  const mealCategorySummaries = useMemo(() => {
    return prijemKategorie
      .map(kategoria => ({ kategoria, totals: getMealTotals(kategoria.id) }))
      .filter(({ totals }) => parseFloat(totals.totalEnergiaKcal) > 0);
  }, [prijemKategorie, prijemByKategoria]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t("intake:title")}</h1>
            <p className="text-muted-foreground">{t("intake:subtitle")}</p>
          </div>

          {selectedKlientId && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Pantry button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSpajzaOpen(true)}
              >
                <ShoppingBasket className="h-4 w-4 mr-2" />
                {t("mealLibrary:spajza.button")}
              </Button>

              {/* Shopping list button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShoppingListOpen(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Nákupný zoznam
              </Button>

              {/* Copy Day button - only for users who can write intake */}
              {canWriteIntake && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCopyDialogOpen(true)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {t("mealLibrary:copy.dayButton")}
                </Button>
              )}

              {/* Print button */}
              <IntakePrint
                printSettings={printSettings}
                onPrintSettingsChange={setPrintSettings}
                areAllColumnsSelected={areAllColumnsSelected}
                onToggleAllColumns={toggleAllColumns}
                onPrint={handlePrintClick}
                disabled={allPrijem.length === 0}
              />

              {/* Edit mode - only for users who can write intake */}
              {canWriteIntake && (
                <Button
                  variant={isEditMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setIsEditMode(!isEditMode);
                    setEditingItem(null);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {isEditMode ? t("intake:doneEditing") : t("intake:editMode")}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <IntakeFilters
          selectedKlientId={selectedKlientId}
          onKlientChange={setSelectedKlientId}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          klienti={klienti}
        />

        {/* Content - Only shown when client is selected */}
        {selectedKlientId && (
          <>
            {/* Daily Summary */}
            <IntakeSummary
              dailyTotals={dailyTotals}
              projectedTotals={projectedDailyTotals}
              mealCategorySummaries={mealCategorySummaries}
              macroTargets={macroTargets as MacroTargets | null | undefined}
            />

            {/* Meal Categories */}
            {prijemKategorie.map(kategoria => {
              const items = prijemByKategoria[kategoria.id] || [];
              const totals = getMealTotals(kategoria.id);
              const projectedTotals = getProjectedMealTotals(kategoria.id);

              return (
                <MealCategoryCard
                  key={kategoria.id}
                  kategoria={kategoria}
                  items={items}
                  potraviny={potraviny}
                  totals={totals}
                  projectedTotals={projectedTotals}
                  isEditMode={canWriteIntake && isEditMode}
                  onAddFood={canWriteIntake ? handleAddFood : undefined}
                  onEditFood={canWriteIntake ? handleEditFood : undefined}
                  onDeleteFood={canWriteIntake ? handleDeleteFood : undefined}
                  onUpdateFood={canWriteIntake ? handleUpdateFood : undefined}
                  editingItem={editingItem}
                  setEditingItem={setEditingItem}
                  onPreviewChange={handlePreviewChange(kategoria.id)}
                  klientId={selectedKlientId}
                  datum={selectedDate}
                  onReorder={canWriteIntake ? handleReorder : undefined}
                  onMoveToKategoria={
                    canWriteIntake ? handleMoveToKategoria : undefined
                  }
                  allKategorie={prijemKategorie}
                />
              );
            })}
          </>
        )}

        {/* No Client Selected Message */}
        {!selectedKlientId && (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-lg">{t("intake:selectClient")}</p>
          </div>
        )}

        {/* Pantry dialog */}
        {selectedKlientId && (
          <SpajzaDialog
            open={spajzaOpen}
            onOpenChange={setSpajzaOpen}
            klientId={selectedKlientId}
            potraviny={potraviny}
          />
        )}

        {/* Shopping list dialog */}
        {selectedKlientId && (
          <ShoppingListDialog
            open={shoppingListOpen}
            onOpenChange={setShoppingListOpen}
            klientId={selectedKlientId}
            datum={selectedDate}
          />
        )}

        {/* Copy intake dialog */}
        {selectedKlientId && (
          <CopyIntakeDialog
            open={copyDialogOpen}
            onOpenChange={setCopyDialogOpen}
            sourceKlientId={selectedKlientId}
            sourceDatum={selectedDate}
            prijemKategorie={prijemKategorie}
            klienti={klienti}
            onCopied={handleCopied}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
